/**
 * Hand Strength Evaluator for Poker Reborn
 *
 * Client-side hand strength calculation. Ported from legacy PokerMath.as.
 * - Preflop: 169 starting hand lookup table → value 1-10
 * - Post-flop: Hand category classifier (royal flush through high card) → value 1-10
 *
 * @see tasks/active/HAND_STRENGTH_METER.md - Research & design
 * @see tasks/active/T39_HAND_STRENGTH_METER_IMPL.md - Implementation plan
 * @see legacy/poker_action_script_4/src/poker/utils/math/PokerMath.as - Original AS3
 */

import type { Card, Rank, Suit } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

/** Internal card representation for the classifier */
interface InternalCard {
  /** Numeric rank: 2-14 (A=14) */
  rank: number;
  /** Bitmask suit: clubs=1, diamonds=2, hearts=4, spades=8 */
  suit: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Rank to numeric value mapping */
const RANK_VALUE: Record<Rank, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, 'T': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

/** Suit to bitmask mapping */
const SUIT_BIT: Record<Suit, number> = {
  'c': 0b0001,  // clubs
  'd': 0b0010,  // diamonds
  'h': 0b0100,  // hearts
  's': 0b1000,  // spades
};

/** All possible pair bitmask combinations (exactly 2 suits OR'd) */
const PAIR_SUITS = [
  0b0011, 0b0101, 0b0110,
  0b1001, 0b1010, 0b1100,
];

/** All possible trips bitmask combinations (exactly 3 suits OR'd) */
const TRIPS_SUITS = [
  0b0111, 0b1011, 0b1101, 0b1110,
];

/** Quads bitmask (all 4 suits OR'd) */
const QUADS_SUIT = 0b1111;

// =============================================================================
// PREFLOP LOOKUP TABLE
// =============================================================================

/**
 * 169 distinct starting hands mapped to strength 1-10.
 * Key format: "HighLow" for pairs, "HighLows" suited, "HighLowo" offsuit.
 * Only non-default entries (value > 1) are listed. All others default to 1.
 *
 * Ported from legacy PokerMath.POCKET_CARDS.
 */
const PREFLOP_TABLE: Record<string, number> = {
  // === Tier 5 (Premium) — value 10 ===
  'AA': 10, 'KK': 10, 'QQ': 10, 'AKs': 10,

  // === Tier 4 (Strong) — value 8 ===
  'JJ': 8, 'AQs': 8, 'AJs': 8, 'KQs': 8,

  // === Tier 3 (Good) — value 5 ===
  'TT': 5, '99': 5,
  'AKo': 5, 'AQo': 5,
  'ATs': 5, 'KJs': 5, 'QJs': 5,

  // === Tier 2 (Average) — value 3 ===
  '88': 3, '77': 3,
  'AJo': 3, 'KQo': 3, 'KJo': 3, 'QJo': 3,
  'KTs': 3, 'QTs': 3, 'JTs': 3, 'A9s': 3,

  // === Tier 1 (Below Average) — value 2 ===
  '66': 2, '55': 2,
  'ATo': 2, 'KTo': 2, 'QTo': 2, 'JTo': 2,
  'K9s': 2, 'Q9s': 2, 'J9s': 2, 'T9s': 2,
  'A8s': 2, 'K8s': 2, 'Q8s': 2, 'J8s': 2, 'T8s': 2, '98s': 2,
  'A7s': 2, 'K7s': 2, 'Q7s': 2, 'J7s': 2, 'T7s': 2, '97s': 2, '87s': 2,
  'A6s': 2, 'K6s': 2, 'Q6s': 2, 'J6s': 2, 'T6s': 2, '96s': 2, '86s': 2, '76s': 2,
  'A5s': 2, 'K5s': 2, 'Q5s': 2, 'J5s': 2, 'T5s': 2, '95s': 2, '85s': 2, '75s': 2, '65s': 2,
  'A4s': 2, 'K4s': 2, 'Q4s': 2, 'J4s': 2, 'T4s': 2, '94s': 2, '84s': 2, '74s': 2, '64s': 2, '54s': 2,
  'A3s': 2, 'K3s': 2, 'Q3s': 2, 'J3s': 2, 'T3s': 2, '93s': 2, '83s': 2, '73s': 2, '63s': 2, '53s': 2, '43s': 2,
  'A2s': 2, 'K2s': 2, 'Q2s': 2, 'J2s': 2, 'T2s': 2, '92s': 2, '82s': 2, '72s': 2, '62s': 2, '52s': 2, '42s': 2, '32s': 2,

  // === Tier 0 (Weak) — value 1 (default, not listed) ===
  // Everything else: low offsuit, low pairs (44, 33, 22), trash offsuit
};

/** Preflop tier names for label display */
const PREFLOP_NAMES: Record<number, string> = {
  1: 'Weak',
  2: 'Below Average',
  3: 'Average',
  5: 'Good',
  8: 'Strong',
  10: 'Premium',
};

/** Post-flop hand category names (matches HAND_NAMES in poker.ts) */
const POSTFLOP_NAMES: Record<number, string> = {
  1: 'High Card',
  2: 'One Pair',
  3: 'Two Pair',
  4: 'Three of a Kind',
  5: 'Straight',
  6: 'Flush',
  7: 'Full House',
  8: 'Four of a Kind',
  9: 'Straight Flush',
  10: 'Royal Flush',
};

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get hand strength value (1-10) for hero's cards.
 * - 2 cards: preflop lookup table
 * - 5+ cards: post-flop hand category classifier
 * - 3-4 cards: returns 0 (no answer)
 */
export function getHandStrength(cards: Card[]): number {
  if (cards.length === 2) {
    return getPreflopStrength(cards[0]!, cards[1]!);
  }
  if (cards.length >= 5) {
    return getPostflopStrength(cards);
  }
  return 0;
}

/**
 * Preflop starting hand lookup (2 cards → 1-10).
 * Normalizes hand to "HighLow" format with suited/offsuit suffix.
 */
export function getPreflopStrength(card1: Card, card2: Card): number {
  const rankValue1 = RANK_VALUE[card1.rank];
  const rankValue2 = RANK_VALUE[card2.rank];

  const highRank = rankValue1 >= rankValue2 ? card1.rank : card2.rank;
  const lowRank = rankValue1 >= rankValue2 ? card2.rank : card1.rank;
  const suited = card1.suit === card2.suit;

  const key = highRank === lowRank
    ? `${highRank}${lowRank}`
    : `${highRank}${lowRank}${suited ? 's' : 'o'}`;

  return PREFLOP_TABLE[key] ?? 1;
}

/**
 * Post-flop hand category classifier (5-7 cards → 1-10).
 * Checks from royal flush down to high card.
 * Ported from legacy PokerMath.getHandRank().
 */
export function getPostflopStrength(cards: Card[]): number {
  if (cards.length < 5) return 0;

  const internal = cards.map(toInternal);

  if (isRoyalFlush(internal))    return 10;
  if (isStraightFlush(internal)) return 9;
  if (isQuads(internal))         return 8;
  if (isFullHouse(internal))     return 7;
  if (isFlush(internal))         return 6;
  if (isStraight(internal))      return 5;
  if (isTrips(internal))         return 4;
  if (isTwoPair(internal))       return 3;
  if (isOnePair(internal))       return 2;
  return 1; // high card
}

/**
 * Get display name for a hand strength value.
 * Uses preflop tier names for values matching preflop tiers,
 * and post-flop category names for post-flop values.
 * @param value - Strength value (1-10)
 * @param isPreflop - Whether this is a preflop evaluation
 */
export function getStrengthName(value: number, isPreflop = false): string {
  if (isPreflop) {
    return PREFLOP_NAMES[value] ?? 'Unknown';
  }
  return POSTFLOP_NAMES[value] ?? 'Unknown';
}

// =============================================================================
// INTERNAL: Card Conversion
// =============================================================================

/** Convert a Card to internal numeric representation */
function toInternal(card: Card): InternalCard {
  return {
    rank: RANK_VALUE[card.rank],
    suit: SUIT_BIT[card.suit],
  };
}

/** Clone an internal card array (deep copy) */
function cloneCards(cards: InternalCard[]): InternalCard[] {
  return cards.map((card) => ({ rank: card.rank, suit: card.suit }));
}

// =============================================================================
// INTERNAL: Sort Helpers
// =============================================================================

/** Sort cards descending by rank (A-high: A=14 is highest) */
function sortByRankDesc(cards: InternalCard[]): InternalCard[] {
  return [...cards].sort((a, b) => b.rank - a.rank);
}

/**
 * Sort cards descending by rank with A-low (A=1 is lowest).
 * Used for detecting A-2-3-4-5 (wheel) straights.
 */
function sortByRankDescAceLow(cards: InternalCard[]): InternalCard[] {
  return [...cards].sort((a, b) => {
    const rankA = a.rank === 14 ? 1 : a.rank;
    const rankB = b.rank === 14 ? 1 : b.rank;
    return rankB - rankA;
  });
}

// =============================================================================
// INTERNAL: Glue By Suit (Key Technique)
// =============================================================================

/**
 * Merge cards of the same rank into a single card with OR'd suit bitmasks.
 * This allows detecting pairs/trips/quads by checking the suit bitmask.
 *
 * Example: K♠ K♥ → single card { rank: 13, suit: 0b1100 }
 *
 * Ported from legacy PokerMath.glueBySuit().
 */
function glueBySuit(cards: InternalCard[]): InternalCard[] {
  const sorted = sortByRankDesc(cloneCards(cards));
  const result: InternalCard[] = [];

  for (const card of sorted) {
    const last = result[result.length - 1];
    if (last && last.rank === card.rank) {
      last.suit = last.suit | card.suit;
    } else {
      result.push(card);
    }
  }

  return result;
}

// =============================================================================
// INTERNAL: Flush Detection
// =============================================================================

/**
 * Find 5+ cards of the same suit. Returns those cards or null.
 * Ported from legacy PokerMath.getFlush().
 */
function getFlush(cards: InternalCard[]): InternalCard[] | null {
  // Count cards per suit
  const suitCounts = new Map<number, InternalCard[]>();
  for (const card of cards) {
    const existing = suitCounts.get(card.suit);
    if (existing) {
      existing.push(card);
    } else {
      suitCounts.set(card.suit, [card]);
    }
  }

  // Find suit with 5+ cards
  for (const suitCards of suitCounts.values()) {
    if (suitCards.length >= 5) {
      return sortByRankDesc(suitCards);
    }
  }

  return null;
}

// =============================================================================
// INTERNAL: Straight Detection
// =============================================================================

/**
 * Find a 5-card straight sequence. Returns the cards or null.
 * Checks both A-high and A-low (wheel) straights.
 * Ported from legacy PokerMath.getStraight().
 */
function getStraight(cards: InternalCard[]): InternalCard[] | null {
  if (cards.length < 5) return null;

  // A-high check: sort descending (A=14 highest)
  const aceHigh = findStraightRun(sortByRankDesc(cards), false);
  if (aceHigh) return aceHigh;

  // A-low check: sort descending with A=1
  const aceLow = findStraightRun(sortByRankDescAceLow(cards), true);
  if (aceLow) return aceLow;

  return null;
}

/**
 * Scan sorted cards for a 5-card consecutive sequence.
 * Cards must already be sorted descending by rank.
 */
function findStraightRun(sorted: InternalCard[], aceLow: boolean): InternalCard[] | null {
  const getRank = (card: InternalCard): number =>
    aceLow && card.rank === 14 ? 1 : card.rank;

  const result: InternalCard[] = [sorted[0]!];

  for (let i = 1; i < sorted.length; i++) {
    const prevRank = getRank(result[result.length - 1]!);
    const currRank = getRank(sorted[i]!);

    if (prevRank - currRank === 1) {
      result.push(sorted[i]!);
      if (result.length === 5) return result;
    } else if (prevRank !== currRank) {
      // Not consecutive and not a duplicate rank — restart
      result.length = 0;
      result.push(sorted[i]!);
    }
    // If prevRank === currRank, skip duplicate rank
  }

  return null;
}

// =============================================================================
// INTERNAL: Pair / Trips Detection (via glued suits)
// =============================================================================

/**
 * Check if a glued card's suit bitmask represents a pair.
 * A pair has exactly 2 suit bits set.
 */
function isPairSuit(suitMask: number): boolean {
  return PAIR_SUITS.includes(suitMask);
}

/**
 * Check if a glued card's suit bitmask represents trips.
 * Trips has exactly 3 suit bits set.
 */
function isTripsSuit(suitMask: number): boolean {
  return TRIPS_SUITS.includes(suitMask);
}

/**
 * Remove the first pair from a glued card array.
 * If hungry=true, also accepts trips as a pair source (for full house detection).
 * Returns remaining cards, or null if no pair found.
 * Ported from legacy PokerMath.cut1Pair().
 */
function cutOnePair(glued: InternalCard[], hungry: boolean): InternalCard[] | null {
  let found = false;
  const result: InternalCard[] = [];

  for (const card of glued) {
    if (!found && (isPairSuit(card.suit) || (hungry && isTripsSuit(card.suit)))) {
      found = true;
      // Skip this card (remove the pair)
    } else {
      result.push(card);
    }
  }

  return found ? result : null;
}

/**
 * Remove the first trips from a glued card array.
 * Returns remaining cards, or null if no trips found.
 * Ported from legacy PokerMath.cut1Trips().
 */
function cutOneTrips(glued: InternalCard[]): InternalCard[] | null {
  let found = false;
  const result: InternalCard[] = [];

  for (const card of glued) {
    if (!found && isTripsSuit(card.suit)) {
      found = true;
      // Skip this card (remove the trips)
    } else {
      result.push(card);
    }
  }

  return found ? result : null;
}

// =============================================================================
// INTERNAL: Hand Category Checkers
// =============================================================================

/** A♦ K♦ Q♦ J♦ T♦ — All same suit, A-K-Q-J-T */
function isRoyalFlush(cards: InternalCard[]): boolean {
  const flushCards = getFlush(cards);
  if (!flushCards) return false;
  const straight = getStraight(flushCards);
  if (!straight) return false;
  // Check if it's A-high straight (A-K-Q-J-T)
  return straight[0]!.rank === 14 && straight[4]!.rank === 10;
}

/** Same suit, consecutive ranks (but not A-K-Q-J-T) */
function isStraightFlush(cards: InternalCard[]): boolean {
  const flushCards = getFlush(cards);
  if (!flushCards) return false;
  return getStraight(flushCards) !== null;
}

/** Four cards of the same rank */
function isQuads(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  return glued.some((card) => card.suit === QUADS_SUIT);
}

/** Three of a kind + a pair */
function isFullHouse(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  const afterTrips = cutOneTrips(glued);
  if (!afterTrips) return false;
  return cutOnePair(afterTrips, true) !== null;
}

/** Five cards of the same suit (not a straight) */
function isFlush(cards: InternalCard[]): boolean {
  return getFlush(cards) !== null;
}

/** Five consecutive ranks (not all same suit) */
function isStraight(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  return getStraight(glued) !== null;
}

/** Three cards of the same rank */
function isTrips(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  return cutOneTrips(glued) !== null;
}

/** Two different pairs */
function isTwoPair(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  const afterFirstPair = cutOnePair(glued, false);
  if (!afterFirstPair) return false;
  return cutOnePair(afterFirstPair, false) !== null;
}

/** Exactly one pair */
function isOnePair(cards: InternalCard[]): boolean {
  const glued = glueBySuit(cards);
  return cutOnePair(glued, false) !== null;
}
